import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axiosConfig';
import io from 'socket.io-client';
import { formatBrasiliaTime } from '../utils/dateUtils';
import useInactivityRedirect from '../hooks/useInactivityRedirect';
import './Checkout.css';

let socket;

const Checkout = () => {
  useInactivityRedirect(3); // Redirect after 3 minutes of inactivity
  
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [trucks, setTrucks] = useState([]);
  const [filteredTrucks, setFilteredTrucks] = useState([]);
  const [message, setMessage] = useState('');

  // Get user role
  const user = JSON.parse(localStorage.getItem('user'));
  const isOperador = user && user.role === 'Operador';

  // Redirect if user is Operador
  useEffect(() => {
    if (isOperador) {
      navigate('/dashboard');
    }
  }, [isOperador, navigate]);

  // Initialize socket connection
  const initSocket = useCallback(() => {
    if (!socket) {
      socket = io('https://nblpatio.nbl.dev.br');
      
      socket.on('connect', () => {
        console.log('Checkout: Socket connected');
      });
      
      socket.on('truckUpdated', (updatedTruck) => {
        console.log('Checkout: Received truck update via socket', updatedTruck);
        
        // Only update if this is a checkout truck (CONCLUIDO status and DESCARREGAMENTO type)
        if (updatedTruck.status === 'CONCLUIDO' && (updatedTruck.tipo === 'DESCARREGAMENTO' || updatedTruck.tipo === 'CARREGAMENTO')) {
          setTrucks(prevTrucks => {
            const truckIndex = prevTrucks.findIndex(t => t._id === updatedTruck._id);
            let newTrucks;
            
            if (truckIndex !== -1) {
              // Truck exists, update it
              newTrucks = [...prevTrucks];
              newTrucks[truckIndex] = updatedTruck;
            } else {
              // New truck, add it
              newTrucks = [...prevTrucks, updatedTruck];
            }
            
            return newTrucks;
          });
        } else {
          // If truck no longer meets criteria, remove it
          setTrucks(prevTrucks => prevTrucks.filter(t => t._id !== updatedTruck._id));
        }
      });
      
      socket.on('disconnect', () => {
        console.log('Checkout: Socket disconnected');
      });
    }
  }, []);

  // Fetch trucks ready for checkout
  useEffect(() => {
    // Don't fetch data if user is Operador
    if (isOperador) return;
    
    const fetchTrucks = async () => {
      try {
        const response = await api.get('/trucks/checkout');
        setTrucks(response.data);
        setFilteredTrucks(response.data);
      } catch (error) {
        console.error('Error fetching trucks:', error);
      }
    };
    
    fetchTrucks();
    
    // Initialize socket connection for real-time updates
    initSocket();
    
    return () => {
      // Clean up socket connection
      if (socket) {
        socket.disconnect();
        socket = null;
      }
    };
  }, [isOperador, initSocket]);

  // Filter trucks based on search term
  useEffect(() => {
    if (isOperador) return;
    
    if (searchTerm) {
      const filtered = trucks.filter(truck => 
        truck.trailerPlate.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTrucks(filtered);
    } else {
      setFilteredTrucks(trucks);
    }
  }, [searchTerm, trucks, isOperador]);

  const handleCheckout = async (truckId) => {
    try {
      // Immediately update UI state
      setTrucks(prevTrucks => prevTrucks.filter(truck => truck._id !== truckId));
      setFilteredTrucks(prevTrucks => prevTrucks.filter(truck => truck._id !== truckId));
      
      // Make API call
      await api.put(`/trucks/${truckId}/checkout`);
      setMessage('Checkout realizado com sucesso!');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      // Revert UI state on error
      // We would need to re-fetch the trucks to restore the deleted one
      // For now, we'll just show the error
      setMessage('Erro ao realizar checkout: ' + (error.response?.data?.message || error.message));
      
      // Re-fetch trucks to restore the UI state
      const response = await api.get('/trucks/checkout');
      setTrucks(response.data);
      setFilteredTrucks(response.data);
    }
  };

  // Don't render anything if user is Operador
  if (isOperador) {
    return null;
  }

  return (
    <div className="checkout-container">
      <h2>Check-out de Motorista</h2>
      
      {message && (
        <div className={`message ${message.includes('sucesso') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}
      
      <div className="search-box">
        <input
          type="text"
          placeholder="Digite as iniciais da placa..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      <div className="truck-list">
        {filteredTrucks.length > 0 ? (
          filteredTrucks.map(truck => (
            <div key={truck._id} className="truck-item">
              <div className="truck-info">
                <span className="order-number">#{truck.orderNumber}</span>
                <span className="plate">🚛 {truck.trailerPlate}</span>
                <span className="partner">🏢 {truck.partner?.name}</span>
                <span className="driver">👨‍✈️ {truck.driverName || 'N/A'}</span>
                <span className="time">
                  Concluído: {formatBrasiliaTime(truck.endTime)}
                </span>
              </div>
              <button 
                className="btn-checkout"
                onClick={() => handleCheckout(truck._id)}
              >
                Confirmar Saída
              </button>
            </div>
          ))
        ) : (
          <div className="no-trucks">
            {searchTerm ? 'Nenhum caminhão encontrado.' : 'Nenhum caminhão pronto para checkout.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;